/**
 * GA4 Server-Side Proxy Client
 * Envia eventos para /api/collect (Cloudflare Pages Function)
 *
 * Batching: eventos são acumulados e enviados em lote a cada 2s
 * ou quando o buffer atinge 10 eventos.
 */

import { debug } from './debug';

const COLLECT_ENDPOINT = '/api/collect';
const BATCH_INTERVAL_MS = 2000;
const BATCH_MAX_SIZE = 10;

interface BatchItem {
  name: string;
  params: Record<string, unknown>;
}

let batch: BatchItem[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let flushPromise: Promise<boolean> | null = null;

function getOrCreateClientId(): string {
  if (typeof window === 'undefined') return 'server';
  const storageKey = '_ga_client_id';
  let clientId: string | null = null;
  try {
    clientId = localStorage.getItem(storageKey);
  } catch {
    /* Safari private mode */
  }
  if (!clientId) {
    const rnd = () => Math.floor(Math.random() * 2147483647);
    clientId = `${rnd()}.${Math.floor(Date.now() / 1000)}`;
    try {
      localStorage.setItem(storageKey, clientId);
    } catch {
      /* ignore */
    }
  }
  return clientId;
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  const key = '_ga_sid';
  try {
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = Math.floor(Date.now() / 1000).toString();
      sessionStorage.setItem(key, id);
    }
    return id;
  } catch {
    return Math.floor(Date.now() / 1000).toString();
  }
}

async function flushBatch(): Promise<boolean> {
  if (batch.length === 0) return true;
  if (flushPromise) return flushPromise;
  const eventsToSend = batch.splice(0, batch.length);
  flushPromise = sendPayload(eventsToSend);
  const result = await flushPromise;
  flushPromise = null;
  return result;
}

async function sendPayload(events: BatchItem[]): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const payload = {
    client_id: getOrCreateClientId(),
    session_id: getOrCreateSessionId(),
    session_number: 1,
    events,
  };
  try {
    const res = await fetch(COLLECT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (!res.ok) {
      debug.warn('[ServerProxy] HTTP', res.status);
      return false;
    }
    debug.log('[ServerProxy] Sent', events.length, 'events');
    return true;
  } catch (err) {
    debug.warn('[ServerProxy] Fetch error:', err);
    return false;
  }
}

function scheduleFlush(): void {
  if (batchTimer) return;
  batchTimer = setTimeout(() => {
    batchTimer = null;
    void flushBatch();
  }, BATCH_INTERVAL_MS);
}

export function enqueueEvent(name: string, params: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  batch.push({ name, params });
  if (batch.length >= BATCH_MAX_SIZE) {
    if (batchTimer) {
      clearTimeout(batchTimer);
      batchTimer = null;
    }
    void flushBatch();
  } else {
    scheduleFlush();
  }
}

export function flushServerEvents(): Promise<boolean> {
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  return flushBatch();
}

// Auto-flush before unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    void flushServerEvents();
  });
}
